import * as React from "react";
import { styled } from "@mui/material";
import clsx from "clsx";
import { UsePatientSummaryApi } from "fe-common/patientSummary/useFetchPatientSummary";
import ShareIcon from "@mui/icons-material/Share";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { ViewOnAgent } from "./viewOnAgent";
import { KAHUN_PLATFORM } from "@kahunmedical/user-management";
import { PatientSummary } from "./patientSummary";
import { PatientSummaryAction } from "./types/SectionAction";
import { useCustomSummaryActions } from "./hooks/useCustomSummaryActions";

//region [[ Styles ]]
const PatientCardSummaryView = styled("div")(({ theme }) => ({
  flex: 2,
  [theme.breakpoints.down("xs")]: {
    padding: 0,
    margin: 0,
  },
}));
const SummaryActionsBar = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap-reverse",
});
const PatientCardActionContainer = styled("div")({
  display: "flex",
  flexFlow: "row-reverse",
  alignItems: "center",
  flexWrap: "wrap-reverse",
  flexBasis: "fit-content",
  flexGrow: 1,
});
//endregion [[ Styles ]]

//region [[ Props ]]
export enum PatientCardCommonActions {
  COPY_TO_CLIPBOARD,
  VIEW_IN_AGENT,
  NONE,
}
export interface PatientCardSummaryProps {
  className?: string;
  patientSummary: UsePatientSummaryApi;
  caseId: string;
  clinicName?: string;
  clinicId?: string;
  supportedSections: string[];
  inExtension?: boolean;
  actions?: PatientCardCommonActions[];
  children?: React.ReactNode;
  onCopy?: (copiedContent: string) => void;
  platform: KAHUN_PLATFORM;
  hideTitle?: boolean;
  sectionActions?: PatientSummaryAction[];
  onNotify?: (message: string) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[Texts]]
const EDIT_TEXT = "EDIT";
const AGENT_LINK_TOOLTIP_TEXT =
  "Patient assessment is a read-only view. For an editable chart, click here. Note, changes made in the editable chart will affect the original patient assessment.";
//endregion [[Texts]]

export const PatientSummaryCommonWrapper = ({
  patientSummary,
  inExtension,
  clinicName,
  clinicId,
  caseId,
  hideTitle,
  sectionActions,
  ...props
}: PatientCardSummaryProps) => {
  const { translate } = useTranslateByGender();
  const { mediatedActions } = useCustomSummaryActions({
    summaryApi: patientSummary,
    sectionActions,
  });

  return (
    <PatientCardSummaryView
      className={clsx("patient-card-summary", props.className)}
    >
      <SummaryActionsBar className={"summary-actions-bar"}>
        <PatientCardActionContainer className={"actions-container"}>
          {props.actions?.includes?.(
            PatientCardCommonActions.VIEW_IN_AGENT,
          ) && (
            <ViewOnAgent
              caseId={caseId}
              label={translate(EDIT_TEXT)}
              icon={<ShareIcon />}
              tooltip={inExtension ? null : AGENT_LINK_TOOLTIP_TEXT}
            />
          )}
        </PatientCardActionContainer>
      </SummaryActionsBar>
      <PatientSummary
        caseId={caseId}
        clinicId={clinicId}
        clinicName={clinicName}
        summary={patientSummary.summary}
        onUpdate={patientSummary.getSummary}
        platform={props.platform}
        onNotify={props.onNotify}
        hideTitle={hideTitle}
        sectionActions={mediatedActions}
        inExtension={inExtension}
        hideCopy
      >
        {props.children}
      </PatientSummary>
    </PatientCardSummaryView>
  );
};
